import React, { useState, useEffect } from 'react';
import { MapPin, Building, DollarSign, Search, Filter } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getJobs } from '../services/admin/jobService';
import { ApplicationForm } from '../components/jobs/ApplicationForm';
import { LoadingSpinner } from '../components/common/LoadingSpinner';

export function JobBoard() {
  const [searchQuery, setSearchQuery] = React.useState('');
  const [jobs, setJobs] = useState<Job[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [isApplicationFormOpen, setIsApplicationFormOpen] = useState(false);
  const { isAuthenticated } = useAuth();
  const [selectedFilters, setSelectedFilters] = React.useState({
    type: [] as string[],
    location: [] as string[],
    salary: [] as string[]
  });

  useEffect(() => {
    loadJobs();
  }, []);

  const loadJobs = async () => {
    try {
      const data = await getJobs();
      setJobs(data.filter(job => job.status === 'active'));
    } catch (error) {
      console.error('Error loading jobs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = 
      job.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      job.company.toLowerCase().includes(searchQuery.toLowerCase()) ||
      job.location.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesType = selectedFilters.type.length === 0 || 
      selectedFilters.type.includes(job.type);

    const matchesLocation = selectedFilters.location.length === 0 || 
      selectedFilters.location.includes(job.location);

    return matchesSearch && matchesType && matchesLocation;
  });

  return (
    <div className="min-h-screen bg-[#ddd9d9] py-8">
      <div className="max-w-7xl mx-auto px-4">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-black">Find Your Next Opportunity</h1>
          <p className="mt-2 text-black">Discover jobs that match your skills and experience</p>
        </div>

        {/* Search and Filters */}
        <div className="mb-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-9">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search jobs by title, company, or location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-background-card border border-gray-800 rounded-xl text-black placeholder-gray-400 focus:ring-2 focus:ring-accent-green focus:border-accent-green"
              />
            </div>
          </div>
          
          <div className="lg:col-span-3">
            <button className="w-full flex items-center justify-center px-4 py-3 bg-background-card border border-gray-800 rounded-xl text-white hover:border-accent-green transition-colors">
              <Filter className="h-4 w-4 mr-2" />
              <span className="text-black">Filter Results</span>
            </button>
          </div>
        </div>
        
        <div className="space-y-6">
          {isLoading && (
            <div className="flex justify-center py-12">
              <LoadingSpinner />
            </div>
          )}
          {!isLoading && filteredJobs.length === 0 && (
            <div className="text-center py-12">
              <p className="text-black">No jobs found matching your criteria</p>
            </div>
          )}
          {!isLoading && filteredJobs.map((job) => (
            <div
              key={job.id}
              className="bg-background-card rounded-xl p-6 shadow-lg transition-all relative overflow-hidden"
            >
              <div className="flex items-start space-x-4">
                <img
                  src={job.logo}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(job.company)}&background=random`;
                  }}
                  alt={`${job.company} logo`}
                  className="w-12 h-12 rounded-lg object-cover bg-gray-100"
                />
                <div className="flex-1">
                  <Link to={`/jobs/${job.id}`}>
                    <h2 className="text-xl font-semibold text-black hover:text-[#d8f477] transition-colors">
                      {job.title}
                    </h2>
                  </Link>
                  <p className="text-black">{job.company}</p>

                  <div className="mt-4 grid grid-cols-3 gap-4">
                    <div className="flex items-center text-black">
                      <MapPin className="h-4 w-4 mr-2" />
                      {job.location}
                    </div>
                    <div className="flex items-center text-black">
                      <Building className="h-4 w-4 mr-2" />
                      {job.type}
                    </div>
                    <div className="flex items-center text-black">
                      <DollarSign className="h-4 w-4 mr-2" />
                      {job.salary}
                    </div>
                  </div>
                </div>
                
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setSelectedJob(job);
                    setIsApplicationFormOpen(true);
                  }}
                  className="flex items-center px-4 py-2 bg-black text-white rounded-lg hover:opacity-90 transition-colors"
                >
                  {isAuthenticated ? 'Apply Now' : 'Sign in to Apply'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Application Form Modal */}
      {selectedJob && isApplicationFormOpen && (
        <ApplicationForm
          job={selectedJob}
          onClose={() => {
            setSelectedJob(null);
            setIsApplicationFormOpen(false);
          }}
          onSuccess={() => {
            setSelectedJob(null);
            setIsApplicationFormOpen(false);
          }}
        />
      )}
    </div>
  );
}